'use client'

import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import { Box, CircularProgress, Tab, Tabs, Typography } from '@mui/material'
import EditNoteIcon from '@mui/icons-material/EditNote'
import ListAltIcon from '@mui/icons-material/ListAlt'

import { PageTitle } from '@/shared/ui/seo/PageTitle'

import { EthicsInfo } from './EthicsInfo'
import { EthicsSubmissionsList } from './EthicsSubmissionsList'
import { EthicsSubmissionForm } from './SubmitEthicsQuestionForm'
import { usePatientEthicsPage } from '../model/usePatientEthicsPage'

export function EthicsPage() {
  const { activeTab, error, ethicsSubmissions, loading, myInfos, setActiveTab } = usePatientEthicsPage()

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 10 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Box sx={{ p: 10, textAlign: 'center' }}>
        <Typography color="error">{error.message}</Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ flex: 1, overflow: 'auto', bgcolor: '#ffffff' }}>
      <PageTitle title={t`Ethical Questions`} />
      <Box component="main" sx={{ py: { xs: 4, sm: 6 }, px: { xs: 2, sm: 3 }, maxWidth: 672, mx: 'auto' }}>
        <Box sx={{ mb: { xs: 3.5, sm: 4 } }}>
          <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.02em', color: '#191c1d', mb: 1.25 }}>
            <Trans>Ethical Questions</Trans>
          </Typography>
          <Box sx={{ height: 5, width: 56, bgcolor: '#7b3fe4', borderRadius: 4, mb: 3 }} />
        </Box>

        <EthicsInfo />

        <Box sx={{ bgcolor: '#f3f4f5', borderRadius: 3, p: 0.5, mb: 3.5 }}>
          <Tabs
            value={activeTab}
            onChange={(_, newValue: number) => setActiveTab(newValue)}
            variant="fullWidth"
            sx={{
              minHeight: 46,
              '& .MuiTabs-indicator': { display: 'none' },
              '& .MuiTab-root': {
                minHeight: 44,
                borderRadius: 2.5,
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '0.95rem',
                color: '#4a4454',
              },
              '& .MuiTab-root.Mui-selected': {
                bgcolor: '#ffffff',
                color: 'info.main',
                boxShadow: '0 12px 40px rgba(25, 28, 29, 0.06)',
              },
            }}
          >
            <Tab icon={<EditNoteIcon />} iconPosition="start" label={t`Submit a question`} />
            <Tab icon={<ListAltIcon />} iconPosition="start" label={t`My questions (${ethicsSubmissions.length})`} />
          </Tabs>
        </Box>

        {activeTab === 0 && myInfos?._id && <EthicsSubmissionForm patientId={myInfos._id} />}

        {activeTab === 1 && <EthicsSubmissionsList submissions={ethicsSubmissions} />}
      </Box>
    </Box>
  )
}
